import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface PrevNextNavigationProps {
  currentPostId: string;
  publishedAt: string;
}

export const PrevNextNavigation = ({ currentPostId, publishedAt }: PrevNextNavigationProps) => {
  const { data: prevPost } = useQuery({
    queryKey: ['blog-prev', currentPostId],
    queryFn: async () => {
      const { data } = await supabase
        .from('blog_posts')
        .select('slug, title, category')
        .eq('is_published', true)
        .lt('published_at', publishedAt)
        .order('published_at', { ascending: false })
        .limit(1)
        .single();
      return data;
    },
  });

  const { data: nextPost } = useQuery({
    queryKey: ['blog-next', currentPostId],
    queryFn: async () => {
      const { data } = await supabase
        .from('blog_posts')
        .select('slug, title, category')
        .eq('is_published', true)
        .gt('published_at', publishedAt)
        .order('published_at', { ascending: true })
        .limit(1)
        .single();
      return data;
    },
  });

  if (!prevPost && !nextPost) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-12">
      {prevPost ? (
        <Link
          to={`/blog/${prevPost.slug}`}
          className="group bg-black/40 backdrop-blur-md border border-white/10 rounded-xl p-5 
            hover:border-[#00C2FF]/40 hover:shadow-[0_0_20px_rgba(0,194,255,0.15)]
            transition-all duration-300"
        >
          <div className="flex items-center gap-2 text-white/50 text-sm mb-2">
            <ChevronLeft className="w-4 h-4" />
            Previous
          </div>
          <p className="text-white font-medium group-hover:text-[#00C2FF] transition-colors line-clamp-2">
            {prevPost.title}
          </p>
          <span className="text-xs text-white/40 mt-1 block">{prevPost.category}</span>
        </Link>
      ) : (
        <div />
      )}
      
      {nextPost ? (
        <Link
          to={`/blog/${nextPost.slug}`}
          className="group bg-black/40 backdrop-blur-md border border-white/10 rounded-xl p-5 
            hover:border-[#00C2FF]/40 hover:shadow-[0_0_20px_rgba(0,194,255,0.15)]
            transition-all duration-300 text-right"
        >
          <div className="flex items-center justify-end gap-2 text-white/50 text-sm mb-2">
            Next
            <ChevronRight className="w-4 h-4" />
          </div>
          <p className="text-white font-medium group-hover:text-[#00C2FF] transition-colors line-clamp-2">
            {nextPost.title}
          </p>
          <span className="text-xs text-white/40 mt-1 block">{nextPost.category}</span>
        </Link>
      ) : (
        <div />
      )}
    </div>
  );
};
